import { endOfDay, parseISO, startOfDay } from "date-fns";

import { TasksFilterQueryParamsV4 } from "@fartherfinance/frontend/api/TaskWorkflows/requests/getTasks";

import type { EnhancedAutocompleteOption } from "@src/multiCustodian/pages/Advisor/common";

import { allServiceTeamOption } from "./TasksFilterForm.const";

import type { FormData } from "./TasksFilterForm";
import type { TaskResourceOptions } from "./useTaskResourceOptions";

const toDate = (value?: string) => (value ? parseISO(value) : undefined);

type TransformFormOptions = Omit<
  TaskResourceOptions,
  "assigneesOptions" | "serviceTeamsOptions"
> & {
  assigneesOptions: EnhancedAutocompleteOption<string>[];
  serviceTeamsOptions: EnhancedAutocompleteOption<string | undefined>[];
};

export const transformFilterQueryParamsToFormValues = ({
  filters,
  options,
}: {
  filters: TasksFilterQueryParamsV4;
  options: TransformFormOptions;
}): FormData => {
  const {
    filterByPersonAssignee,
    filterByGroupAssignee,
    filterByAdvisor,
    filterByClient,
    filterByCreatedAfter,
    filterByCreatedBefore,
    filterByUpdatedAfter,
    filterByUpdatedBefore,
    filterByDueDateAfter,
    filterByDueDateBefore,
    filterByTaskStatus,
    filterByTaskType,
    filterByTaskPriority,
    filterByCustodian,
    filterByTaskSource,
    filterByServiceTeam,
  } = filters;

  const {
    clientsOptions,
    advisorsOptions,
    assigneesOptions,
    serviceTeamsOptions,
    taskTypesOptions,
    taskStatusesOptions,
    taskPrioritiesOptions,
    generationOptions,
    custodianOptions,
  } = options;

  const filterByPersonAssigneeValue = filterByPersonAssignee
    ? assigneesOptions.filter((assignee) =>
        filterByPersonAssignee.includes(assignee.value!)
      )
    : [];

  const containsNull = filterByGroupAssignee?.includes("null");

  const filterByGroupAssigneeValue =
    filterByGroupAssignee && !containsNull
      ? assigneesOptions.filter((assignee) =>
          filterByGroupAssignee.includes(assignee.value!)
        )
      : [];

  return {
    filterByAssignee: filterByPersonAssigneeValue.concat(
      filterByGroupAssigneeValue
    ),
    filterByAdvisor: filterByAdvisor
      ? advisorsOptions.filter((advisor) =>
          filterByAdvisor.includes(advisor.value)
        )
      : [],
    filterByClient: filterByClient
      ? clientsOptions.filter((client) => filterByClient.includes(client.value))
      : [],
    filterByTaskStatus: filterByTaskStatus
      ? taskStatusesOptions.filter((taskStatus) =>
          filterByTaskStatus.includes(taskStatus.value)
        )
      : [],
    filterByTaskType: filterByTaskType
      ? taskTypesOptions.filter((taskType) =>
          filterByTaskType.includes(taskType.value)
        )
      : [],
    filterByTaskPriority: filterByTaskPriority
      ? taskPrioritiesOptions.filter((taskPriority) =>
          filterByTaskPriority.includes(taskPriority.value)
        )
      : [],
    filterByCustodian: filterByCustodian
      ? custodianOptions.filter((custodian) =>
          filterByCustodian.includes(custodian.value)
        )
      : [],
    filterByTaskSource: filterByTaskSource
      ? generationOptions.filter((generation) =>
          filterByTaskSource.includes(generation.value)
        )
      : [],
    filterByServiceTeam: filterByServiceTeam
      ? serviceTeamsOptions.filter(
          (serviceTeam) =>
            serviceTeam.value !== undefined &&
            filterByServiceTeam.includes(serviceTeam.value)
        )
      : [allServiceTeamOption],
    filterByCreatedAfter: toDate(filterByCreatedAfter),
    filterByCreatedBefore: toDate(filterByCreatedBefore),
    filterByUpdatedAfter: toDate(filterByUpdatedAfter),
    filterByUpdatedBefore: toDate(filterByUpdatedBefore),
    filterByDueDateAfter: toDate(filterByDueDateAfter),
    filterByDueDateBefore: toDate(filterByDueDateBefore),
  };
};

export const transformFormValuesToFilterQueryParams = (
  formData: FormData
): TasksFilterQueryParamsV4 => {
  const {
    filterByAssignee,
    filterByAdvisor,
    filterByClient,
    filterByCreatedAfter,
    filterByCreatedBefore,
    filterByUpdatedAfter,
    filterByUpdatedBefore,
    filterByDueDateAfter,
    filterByDueDateBefore,
    filterByTaskStatus,
    filterByTaskType,
    filterByTaskPriority,
    filterByCustodian,
    filterByTaskSource,
    filterByServiceTeam,
  } = formData;

  const hasUnassignedOption = filterByAssignee
    ? filterByAssignee.find((assignee) => assignee.value === "null")
    : false;

  const filterByPersonAssignee = filterByAssignee
    ? filterByAssignee.map((assignee) => assignee.value)
    : [];

  const filterByAssignees = hasUnassignedOption
    ? { filterByPersonAssignee, filterByGroupAssignee: ["null"] }
    : {
        filterByPersonAssignee: filterByPersonAssignee.length
          ? filterByPersonAssignee
          : undefined,
      };

  return {
    ...filterByAssignees,
    filterByAdvisor:
      filterByAdvisor && filterByAdvisor.length
        ? filterByAdvisor.map((advisor) => advisor.value)
        : undefined,
    filterByClient:
      filterByClient && filterByClient.length
        ? filterByClient.map((client) => client.value)
        : undefined,
    filterByTaskStatus:
      filterByTaskStatus && filterByTaskStatus.length
        ? filterByTaskStatus.map((taskStatus) => taskStatus.value)
        : undefined,
    filterByTaskType:
      filterByTaskType && filterByTaskType.length
        ? filterByTaskType.map((taskType) => taskType.value)
        : undefined,
    filterByTaskPriority:
      filterByTaskPriority && filterByTaskPriority.length
        ? filterByTaskPriority.map((taskPriority) => taskPriority.value)
        : undefined,
    filterByCustodian:
      filterByCustodian && filterByCustodian.length
        ? filterByCustodian.map((custodian) => custodian.value)
        : undefined,
    filterByTaskSource:
      filterByTaskSource && filterByTaskSource.length
        ? filterByTaskSource.map((source) => source.value)
        : undefined,

    filterByCreatedAfter: filterByCreatedAfter
      ? startOfDay(filterByCreatedAfter).toISOString()
      : undefined,
    filterByCreatedBefore: filterByCreatedBefore
      ? endOfDay(filterByCreatedBefore).toISOString()
      : undefined,
    filterByUpdatedAfter: filterByUpdatedAfter
      ? startOfDay(filterByUpdatedAfter).toISOString()
      : undefined,
    filterByUpdatedBefore: filterByUpdatedBefore
      ? endOfDay(filterByUpdatedBefore).toISOString()
      : undefined,
    filterByDueDateAfter: filterByDueDateAfter
      ? startOfDay(filterByDueDateAfter).toISOString()
      : undefined,
    filterByDueDateBefore: filterByDueDateBefore
      ? endOfDay(filterByDueDateBefore).toISOString()
      : undefined,
    filterByServiceTeam:
      filterByServiceTeam && filterByServiceTeam.length
        ? filterByServiceTeam.map((s) => s.value).includes(undefined)
          ? undefined
          : filterByServiceTeam.map((serviceTeam) => serviceTeam.value!)
        : undefined,
  };
};
